/**
 * 템플릿 블록 구조 미리보기: 서버 조각 HTML 을 디바운스 fetch 로 갱신.
 */
import Sortable from "sortablejs";

import { initColorPickerFields } from "../components/colorPickerField";

export interface TemplatePreviewBlockPayload {
    section_slug: string;
    variant?: string;
}

export class WorkTemplatePreviewPanel {
    readonly #pageRoot: HTMLElement;
    readonly #debounceMs: number;
    readonly #previewUrl: string;
    readonly #previewRenderUrl: string;
    readonly #previewRenderDraftUrl: string;
    readonly #previewSaveTextUrl: string;
    readonly #uploadAttachmentUrl: string;
    readonly #previewSaveAttachmentUrl: string;
    readonly #previewReorderImageListUrl: string;
    readonly #previewDeleteImageListItemUrl: string;
    readonly #previewSaveImageListMetaUrl: string;
    readonly #previewWorkId: number;

    #list: HTMLElement | null = null;
    #form: HTMLFormElement | null = null;
    #body: HTMLElement | null = null;
    #loading: HTMLElement | null = null;
    #error: HTMLElement | null = null;
    #tabsMounted = false;
    #cmsBody: HTMLElement | null = null;
    #cmsError: HTMLElement | null = null;

    #timer: ReturnType<typeof setTimeout> | null = null;
    #abort: AbortController | null = null;
    #cmsAbort: AbortController | null = null;

    readonly #onVariantChange = (ev: Event): void => {
        const t = ev.target;
        if (t instanceof HTMLSelectElement && t.classList.contains("admin-work-blocks__variant-select")) {
            this.scheduleRefresh();
        }
    };

    constructor(pageRoot: HTMLElement, debounceMs = 320) {
        this.#pageRoot = pageRoot;
        this.#debounceMs = debounceMs;
        this.#previewUrl = pageRoot.dataset.previewUrl?.trim() ?? "";
        this.#previewRenderUrl = pageRoot.dataset.previewRenderUrl?.trim() ?? "";
        this.#previewRenderDraftUrl = pageRoot.dataset.previewRenderDraftUrl?.trim() ?? "";
        this.#previewSaveTextUrl = pageRoot.dataset.previewSaveTextUrl?.trim() ?? "";
        this.#uploadAttachmentUrl = pageRoot.dataset.uploadAttachmentUrl?.trim() ?? "";
        this.#previewSaveAttachmentUrl = pageRoot.dataset.previewSaveAttachmentUrl?.trim() ?? "";
        this.#previewReorderImageListUrl = pageRoot.dataset.previewReorderImageListUrl?.trim() ?? "";
        this.#previewDeleteImageListItemUrl = pageRoot.dataset.previewDeleteImageListItemUrl?.trim() ?? "";
        this.#previewSaveImageListMetaUrl = pageRoot.dataset.previewSaveImageListMetaUrl?.trim() ?? "";
        this.#previewWorkId = Number(pageRoot.dataset.previewWorkId ?? "0") || 0;
    }

    init(list: HTMLElement, form: HTMLFormElement): void {
        const aside = this.#pageRoot.querySelector<HTMLElement>("[data-work-template-preview]");
        this.#loading = aside?.querySelector<HTMLElement>("[data-work-template-preview-loading]") ?? null;
        this.#cmsBody = aside?.querySelector<HTMLElement>("[data-work-template-preview-cms-body]") ?? null;
        this.#cmsError = aside?.querySelector<HTMLElement>("[data-work-template-preview-cms-error]") ?? null;
        const refreshBtn = aside?.querySelector<HTMLButtonElement>("[data-work-template-canvas-refresh]") ?? null;

        if (!this.#cmsBody || !this.#loading || !this.#cmsError) return;

        this.#list = list;
        this.#form = form;

        this.#pageRoot.addEventListener("change", this.#onVariantChange);
        refreshBtn?.addEventListener("click", () => void this.#loadCmsPreview());
        // 최초 1회는 즉시 렌더
        void this.#loadCmsPreview();
    }

    scheduleRefresh(): void {
        if (!this.#list || !this.#form || !this.#cmsBody) return;

        if (this.#timer !== null) clearTimeout(this.#timer);
        // 구조 변화 시 캔버스 리렌더
        this.#timer = setTimeout(() => void this.#loadCmsPreview(), this.#debounceMs);
    }

    static collectPayload(list: HTMLElement): TemplatePreviewBlockPayload[] {
        const items = list.querySelectorAll<HTMLElement>("[data-work-block-item]");
        const out: TemplatePreviewBlockPayload[] = [];
        items.forEach((item) => {
            const slug = item.dataset.sectionSlug?.trim() ?? "";
            if (!slug) return;
            const sel = item.querySelector<HTMLSelectElement>(".admin-work-blocks__variant-select");
            const variant = sel?.value?.trim() ?? "";
            if (variant !== "") {
                out.push({ section_slug: slug, variant });
            } else {
                out.push({ section_slug: slug });
            }
        });
        return out;
    }

    // 구조 프래그먼트(와이어프레임) 로직은 제거하고, 캔버스 렌더만 사용한다.

    async #loadCmsPreview(): Promise<void> {
        const form = this.#form;
        const list = this.#list;
        const cmsBody = this.#cmsBody;
        const cmsError = this.#cmsError;
        const loading = this.#loading;
        if (!form || !list || !cmsBody || !cmsError) return;

        // create 단계: previewWork 가 없으면 draft 렌더만 제공 (저장은 edit 이후 가능)
        const isDraft = this.#previewWorkId <= 0;
        const renderUrl = isDraft ? this.#previewRenderDraftUrl : this.#previewRenderUrl;
        if (renderUrl === "") {
            cmsBody.innerHTML =
                `<div class="admin-work-templates-preview-empty"><p class="admin-work-templates-preview-empty__text">미리보기를 불러올 수 없습니다.</p></div>`;
            return;
        }

        const token = form.querySelector<HTMLInputElement>('input[name="_token"]')?.value ?? "";
        if (!token) {
            cmsError.textContent = "CSRF 토큰을 찾을 수 없어 미리보기를 불러오지 못했습니다. 페이지를 새로고침 해주세요.";
            cmsError.hidden = false;
            return;
        }

        this.#cmsAbort?.abort();
        this.#cmsAbort = new AbortController();

        cmsError.hidden = true;
        cmsError.textContent = "";
        if (loading) {
            loading.hidden = false;
            loading.style.display = "flex";
        }
        cmsBody.innerHTML =
            `<div class="admin-work-templates-preview-empty"><p class="admin-work-templates-preview-empty__text">CMS 미리보기를 불러오는 중…</p></div>`;

        const fd = new FormData();
        fd.append("_token", token);
        if (!isDraft) {
            fd.append("work_id", String(this.#previewWorkId));
        }
        fd.append("blocks", JSON.stringify(WorkTemplatePreviewPanel.collectPayload(list)));

        try {
            // timeout guard (so we don't get stuck in "loading" forever)
            const timeoutMs = 8000;
            const timeoutId = window.setTimeout(() => {
                this.#cmsAbort?.abort();
            }, timeoutMs);

            const res = await fetch(renderUrl, {
                method: "POST",
                body: fd,
                signal: this.#cmsAbort.signal,
                headers: {
                    Accept: "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                },
            });
            window.clearTimeout(timeoutId);
            if (!res.ok) {
                cmsError.textContent = `CMS 미리보기를 불러오지 못했습니다. (HTTP ${res.status})`;
                cmsError.hidden = false;
                cmsBody.innerHTML = `<div class="admin-work-templates-preview-empty"><p class="admin-work-templates-preview-empty__text">${cmsError.textContent}</p></div>`;
                return;
            }
            const data = (await res.json()) as { result?: string; html?: string; message?: string };
            if (data.result === "success" && typeof data.html === "string") {
                cmsBody.innerHTML = data.html;
                if (isDraft) {
                    // 드래프트는 렌더만. 저장/업로드는 edit(저장 후)에서.
                    cmsBody.insertAdjacentHTML(
                        "afterbegin",
                        `<div class="admin-work-templates-preview-empty"><p class="admin-work-templates-preview-empty__text">create 단계에서는 캔버스 미리보기만 제공됩니다. 템플릿을 저장한 뒤 편집 화면에서 텍스트/미디어 저장이 가능합니다.</p></div>`,
                    );
                } else {
                    this.#bindCanvasBlockSorter(cmsBody);
                    this.#bindInlineTextEditors(cmsBody, token);
                    this.#bindInlineMediaUploaders(cmsBody, token);
                    this.#bindImageListManagers(cmsBody, token);
                    initColorPickerFields(cmsBody, {
                        workId: this.#previewWorkId,
                        saveUrl: this.#previewSaveTextUrl,
                        csrfToken: token,
                    });
                }
                return;
            }
            cmsError.textContent = data.message || "CMS 미리보기를 불러오지 못했습니다.";
            cmsError.hidden = false;
            cmsBody.innerHTML = `<div class="admin-work-templates-preview-empty"><p class="admin-work-templates-preview-empty__text">${cmsError.textContent}</p></div>`;
        } catch (e) {
            if ((e as Error).name === "AbortError") return;
            cmsError.textContent = "네트워크/타임아웃 오류로 CMS 미리보기를 불러오지 못했습니다.";
            cmsError.hidden = false;
            cmsBody.innerHTML = `<div class="admin-work-templates-preview-empty"><p class="admin-work-templates-preview-empty__text">${cmsError.textContent}</p></div>`;
        } finally {
            if (loading) {
                loading.hidden = true;
                loading.style.display = "none";
            }
        }
    }

    /**
     * 캔버스에서 섹션 순서 변경(드래그)을 허용하고,
     * 변경 결과를 hidden 구조 list(#list) DOM 순서로 동기화한다.
     */
    #bindCanvasBlockSorter(root: HTMLElement): void {
        if (!this.#list) return;
        const container = root.querySelector<HTMLElement>(".admin-work-templates-cms-preview");
        if (!container) return;
        if (container.dataset.cmsSortableBound === "1") return;
        container.dataset.cmsSortableBound = "1";

        Sortable.create(container, {
            animation: 150,
            draggable: "[data-cms-block]",
            handle: "[data-cms-block-drag]",
            ghostClass: "admin-sortable__ghost",
            dragClass: "admin-sortable__drag",
            onEnd: () => {
                this.#syncStructureListFromCanvas(container);
                // 캔버스에서 이미 순서가 바뀌었으므로 즉시 리렌더(깜빡임)하지 않는다.
                // 저장 시(#list 기반 payload) 순서가 반영된다.
            },
        });
    }

    #syncStructureListFromCanvas(container: HTMLElement): void {
        const list = this.#list;
        if (!list) return;

        const desiredIds: number[] = [];
        container.querySelectorAll<HTMLElement>("[data-cms-block]").forEach((el) => {
            const id = Number(el.dataset.blockId ?? "0") || 0;
            if (id > 0) desiredIds.push(id);
        });
        if (desiredIds.length === 0) return;

        const items = Array.from(list.querySelectorAll<HTMLElement>("[data-work-block-item]"));
        const byId = new Map<number, HTMLElement>();
        for (const item of items) {
            const idInput = item.querySelector<HTMLInputElement>('input[name$="[id]"]');
            const id = Number(idInput?.value ?? "0") || 0;
            if (id > 0) byId.set(id, item);
        }

        const ordered: HTMLElement[] = [];
        for (const id of desiredIds) {
            const node = byId.get(id);
            if (node) ordered.push(node);
        }
        // append any leftovers (newly added but not in canvas yet)
        for (const item of items) {
            if (!ordered.includes(item)) ordered.push(item);
        }

        for (const el of ordered) list.appendChild(el);
    }

    #bindInlineTextEditors(root: HTMLElement, token: string): void {
        if (this.#previewSaveTextUrl === "" || this.#previewWorkId <= 0) return;
        root.querySelectorAll<HTMLElement>(
            "[data-field-key][data-field-type='text'],[data-field-key][data-field-type='richtext']",
        ).forEach((el) => {
            if (el.dataset.cmsBound === "1") return;
            el.dataset.cmsBound = "1";
            el.tabIndex = 0;
            el.addEventListener("click", () => {
                el.contentEditable = "true";
                el.focus();
            });
            el.addEventListener("focus", () => {
                el.classList.add("is-cms-editing");
            });
            el.addEventListener("blur", () => {
                el.classList.remove("is-cms-editing");
                el.contentEditable = "false";
                void this.#saveInlineText(el, token);
            });
        });
    }

    #bindInlineMediaUploaders(root: HTMLElement, token: string): void {
        if (
            this.#uploadAttachmentUrl === "" ||
            this.#previewSaveAttachmentUrl === "" ||
            this.#previewWorkId <= 0
        ) {
            return;
        }

        // image / video: 클릭 시 단일 업로드
        root.querySelectorAll<HTMLElement>("[data-field-key][data-field-type='image'],[data-field-key][data-field-type='video']").forEach((el) => {
            if (el.dataset.cmsMediaBound === "1") return;
            el.dataset.cmsMediaBound = "1";
            el.addEventListener("click", (e) => {
                e.preventDefault();
                e.stopPropagation();
                void this.#pickAndUpload(el, token, false);
            });
            this.#bindDropzone(el, token);
        });

        // image_list: 컨테이너 클릭 → 다중 업로드(선택 가능), append
        root.querySelectorAll<HTMLElement>("[data-field-key][data-field-type='image_list']").forEach((el) => {
            if (el.dataset.cmsMediaBound === "1") return;
            el.dataset.cmsMediaBound = "1";
            el.addEventListener("click", (e) => {
                e.preventDefault();
                e.stopPropagation();
                void this.#pickAndUpload(el, token, true);
            });
            this.#bindDropzone(el, token);
        });
    }

    async #pickAndUpload(el: HTMLElement, token: string, multiple: boolean): Promise<void> {
        const type = (el.dataset.fieldType ?? "").trim();
        const blockId = Number(el.dataset.fieldBlockId ?? "0") || 0;
        const fieldKey = (el.dataset.fieldKey ?? "").trim();
        if (!type || blockId <= 0 || !fieldKey) return;

        const input = document.createElement("input");
        input.type = "file";
        input.multiple = multiple;
        input.accept =
            type === "video" ? ".mp4,.webm,.ogg,.mov" : ".png,.jpg,.jpeg,.webp,.gif";
        input.click();

        await new Promise<void>((resolve) => {
            input.addEventListener("change", () => resolve(), { once: true });
        });

        const files = input.files ? Array.from(input.files) : [];
        if (files.length === 0) return;

        // 순차 업로드 (서버 부하/순서 보장)
        for (const file of files) {
            const attachmentId = await this.#uploadAttachment(file, token, type === "video" ? "video" : "image");
            if (!attachmentId) continue;
            await this.#saveAttachmentLink(token, {
                blockId,
                fieldKey,
                valueType: type,
                attachmentId,
            });
        }

        void this.#loadCmsPreview();
    }

    async #uploadAttachment(file: File, token: string, category: "image" | "video"): Promise<number> {
        const fd = new FormData();
        fd.append("_token", token);
        fd.append("file", file);
        fd.append("category", category);
        fd.append("target_type", "temp");
        fd.append("target_id", "0");

        try {
            const res = await fetch(this.#uploadAttachmentUrl, {
                method: "POST",
                body: fd,
                headers: {
                    Accept: "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                },
            });
            if (!res.ok) return 0;
            const data = (await res.json()) as { result?: string; data?: { id?: number } };
            const id = data?.data?.id ?? 0;
            return typeof id === "number" ? id : 0;
        } catch {
            return 0;
        }
    }

    async #saveAttachmentLink(
        token: string,
        args: { blockId: number; fieldKey: string; valueType: string; attachmentId: number },
    ): Promise<void> {
        const fd = new FormData();
        fd.append("_token", token);
        fd.append("work_id", String(this.#previewWorkId));
        fd.append("block_id", String(args.blockId));
        fd.append("field_key", args.fieldKey);
        fd.append("value_type", args.valueType);
        fd.append("attachment_id", String(args.attachmentId));

        try {
            await fetch(this.#previewSaveAttachmentUrl, {
                method: "POST",
                body: fd,
                headers: {
                    Accept: "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                },
            });
        } catch {
            // no-op
        }
    }

    async #saveInlineText(el: HTMLElement, token: string): Promise<void> {
        const blockId = Number(el.dataset.fieldBlockId ?? "0") || 0;
        const fieldKey = (el.dataset.fieldKey ?? "").trim();
        const type = (el.dataset.fieldType ?? "").trim();
        if (blockId <= 0 || fieldKey === "") return;

        const valueText = type === "richtext" ? el.innerHTML : (el.textContent ?? "");
        const valueType = type === "richtext" ? "richtext" : "text";

        const fd = new FormData();
        fd.append("_token", token);
        fd.append("work_id", String(this.#previewWorkId));
        fd.append("block_id", String(blockId));
        fd.append("field_key", fieldKey);
        fd.append("value_type", valueType);
        fd.append("value_text", valueText);

        try {
            const res = await fetch(this.#previewSaveTextUrl, {
                method: "POST",
                body: fd,
                headers: {
                    Accept: "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                },
            });
            if (!res.ok) return;
            const data = (await res.json()) as { result?: string };
            if (data.result === "success") {
                void this.#loadCmsPreview();
            }
        } catch {
            // no-op
        }
    }

    #bindImageListManagers(root: HTMLElement, token: string): void {
        if (
            this.#previewReorderImageListUrl === "" ||
            this.#previewDeleteImageListItemUrl === "" ||
            this.#previewSaveImageListMetaUrl === "" ||
            this.#previewWorkId <= 0
        ) {
            return;
        }

        // image_list 컨테이너(ul)에 sortable + 삭제 버튼 + 캡션 편집을 부여
        root.querySelectorAll<HTMLElement>("[data-field-key][data-field-type='image_list']").forEach((listEl) => {
            if (listEl.dataset.cmsListBound === "1") return;
            listEl.dataset.cmsListBound = "1";

            // Sortable: li 단위로 순서 변경
            Sortable.create(listEl, {
                animation: 150,
                draggable: "li",
                onEnd: () => {
                    void this.#saveImageListOrder(listEl, token);
                },
            });

            // 삭제 버튼: img[data-field-attachment-id] 기준
            listEl.querySelectorAll<HTMLImageElement>("img[data-field-attachment-id]").forEach((img) => {
                const attId = Number(img.dataset.fieldAttachmentId ?? "0") || 0;
                if (attId <= 0) return;
                const li = img.closest("li") as HTMLElement | null;
                if (!li) return;
                if (li.querySelector("[data-cms-delete-item]")) return;

                li.style.position = li.style.position || "relative";
                const btn = document.createElement("button");
                btn.type = "button";
                btn.textContent = "×";
                btn.className = "admin-cms-media__delete";
                btn.setAttribute("data-cms-delete-item", "1");
                btn.setAttribute("aria-label", "이미지 삭제");
                btn.addEventListener("click", (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    void this.#deleteImageListItem(listEl, attId, token);
                });
                li.appendChild(btn);
            });

            // 캡션 인라인 편집
            listEl.querySelectorAll<HTMLElement>("[data-field-caption][data-field-attachment-id]").forEach((cap) => {
                if (cap.dataset.cmsCaptionBound === "1") return;
                cap.dataset.cmsCaptionBound = "1";
                const attId = Number(cap.dataset.fieldAttachmentId ?? "0") || 0;
                if (attId <= 0) return;
                cap.tabIndex = 0;
                cap.addEventListener("click", (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    cap.contentEditable = "true";
                    cap.focus();
                });
                cap.addEventListener("blur", () => {
                    cap.contentEditable = "false";
                    void this.#saveImageListMeta(listEl, attId, { caption: cap.textContent ?? "" }, token);
                });
            });
        });
    }

    async #saveImageListOrder(listEl: HTMLElement, token: string): Promise<void> {
        const blockId = Number(listEl.dataset.fieldBlockId ?? "0") || 0;
        const fieldKey = (listEl.dataset.fieldKey ?? "").trim();
        if (blockId <= 0 || !fieldKey) return;

        const ids: number[] = [];
        listEl.querySelectorAll<HTMLImageElement>("img[data-field-attachment-id]").forEach((img) => {
            const id = Number(img.dataset.fieldAttachmentId ?? "0") || 0;
            if (id > 0) ids.push(id);
        });
        if (ids.length === 0) return;

        const fd = new FormData();
        fd.append("_token", token);
        fd.append("work_id", String(this.#previewWorkId));
        fd.append("block_id", String(blockId));
        fd.append("field_key", fieldKey);
        ids.forEach((id) => fd.append("attachment_ids[]", String(id)));

        try {
            await fetch(this.#previewReorderImageListUrl, {
                method: "POST",
                body: fd,
                headers: {
                    Accept: "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                },
            });
        } catch {
            // no-op
        }
    }

    async #deleteImageListItem(listEl: HTMLElement, attachmentId: number, token: string): Promise<void> {
        const blockId = Number(listEl.dataset.fieldBlockId ?? "0") || 0;
        const fieldKey = (listEl.dataset.fieldKey ?? "").trim();
        if (blockId <= 0 || !fieldKey) return;

        const fd = new FormData();
        fd.append("_token", token);
        fd.append("work_id", String(this.#previewWorkId));
        fd.append("block_id", String(blockId));
        fd.append("field_key", fieldKey);
        fd.append("attachment_id", String(attachmentId));

        try {
            await fetch(this.#previewDeleteImageListItemUrl, {
                method: "POST",
                body: fd,
                headers: {
                    Accept: "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                },
            });
        } catch {
            // no-op
        }
        void this.#loadCmsPreview();
    }

    async #saveImageListMeta(
        listEl: HTMLElement,
        attachmentId: number,
        meta: { caption?: string; alt?: string },
        token: string,
    ): Promise<void> {
        const blockId = Number(listEl.dataset.fieldBlockId ?? "0") || 0;
        const fieldKey = (listEl.dataset.fieldKey ?? "").trim();
        if (blockId <= 0 || !fieldKey) return;

        const fd = new FormData();
        fd.append("_token", token);
        fd.append("work_id", String(this.#previewWorkId));
        fd.append("block_id", String(blockId));
        fd.append("field_key", fieldKey);
        fd.append("attachment_id", String(attachmentId));
        fd.append("meta_json", JSON.stringify(meta));

        try {
            await fetch(this.#previewSaveImageListMetaUrl, {
                method: "POST",
                body: fd,
                headers: {
                    Accept: "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                },
            });
        } catch {
            // no-op
        }
    }

    #bindDropzone(el: HTMLElement, token: string): void {
        if (el.dataset.fieldDropBound === "1") return;
        el.dataset.fieldDropBound = "1";
        el.addEventListener("dragover", (e) => {
            e.preventDefault();
            if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
            el.classList.add("is-drop-over");
        });
        el.addEventListener("dragleave", () => {
            el.classList.remove("is-drop-over");
        });
        el.addEventListener("drop", (e) => {
            e.preventDefault();
            el.classList.remove("is-drop-over");
            const files = e.dataTransfer ? Array.from(e.dataTransfer.files ?? []) : [];
            if (!files.length) return;
            const multiple = (el.dataset.fieldType ?? "") === "image_list";
            void this.#uploadDroppedFiles(el, token, files, multiple);
        });
    }

    async #uploadDroppedFiles(el: HTMLElement, token: string, files: File[], multiple: boolean): Promise<void> {
        const type = (el.dataset.fieldType ?? "").trim();
        const blockId = Number(el.dataset.fieldBlockId ?? "0") || 0;
        const fieldKey = (el.dataset.fieldKey ?? "").trim();
        if (!type || blockId <= 0 || !fieldKey) return;
        const queue = multiple ? files : files.slice(0, 1);
        for (const file of queue) {
            const attachmentId = await this.#uploadAttachment(file, token, type === "video" ? "video" : "image");
            if (!attachmentId) continue;
            await this.#saveAttachmentLink(token, {
                blockId,
                fieldKey,
                valueType: type,
                attachmentId,
            });
        }
        void this.#loadCmsPreview();
    }
}
